/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import React, { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';

import { RecordingStatus } from 'client-code/previews';

import { STAR_RATING_MAX_STARS } from '../../../config';
import { DeviceFeatures } from '../../../core/device';
import { DimensionScaler } from '../../../core/device/DimensionScaler';
import { Locale, LocaleKeys } from '../../../core/locale';
import { TiVoMemo } from '../../../core/tivo/tivo-react-wrapper';
import { TOfferPreviewCustomData } from '../../../core/types';
import { OfferPreviewFormatter } from '../../common/OfferPreviewFormatter';
import { ImageLoader } from '../../ImageLoader';
import { ImageLoaderTypes } from '../../ImageLoader/ImageLoaderTypes';
import PreviewImage from './PreviewImage/PreviewImage';

import './Preview.scss';

const Preview: FunctionComponent<{ offer: TOfferPreviewCustomData }> = ({ offer }): JSX.Element => {
    const previewRef = useRef<HTMLDivElement>(null);
    const topSectionRef = useRef<HTMLDivElement>(null);
    const middleSectionRef = useRef<HTMLDivElement>(null);
    const bottomSectionRef = useRef<HTMLDivElement>(null);

    const titleRef = useRef<HTMLDivElement>(null);
    const episodeTitleRef = useRef<HTMLDivElement>(null);
    const descriptionRef = useRef<HTMLDivElement>(null);
    const parentalGeneresRef = useRef<HTMLDivElement>(null);

    const [isPreviewOverflowedInY, setIsPreviewOverflowedInY] = useState(false);
    const [descriptionHeight, setDescriptionHeight] = useState(0);
    const [chopItemsForSpaceInY, setChopItemsForSpaceInY] = useState(false);

    const HEIGHT_OF_ONE_LINE_OF_DESCRIPTION_SECTION = DimensionScaler.getInstance().getScaledHeight(
        41.5,
        false,
    );

    const resize = () => {
        if (
            previewRef.current &&
            topSectionRef.current &&
            middleSectionRef.current &&
            bottomSectionRef.current
        ) {
            const overFlow = previewRef.current.scrollHeight > previewRef.current.clientHeight;
            setIsPreviewOverflowedInY(overFlow);

            let descHeight =
                previewRef.current?.clientHeight -
                (topSectionRef.current?.clientHeight + bottomSectionRef.current?.clientHeight);

            if (descHeight < HEIGHT_OF_ONE_LINE_OF_DESCRIPTION_SECTION) {
                descHeight = HEIGHT_OF_ONE_LINE_OF_DESCRIPTION_SECTION;
                setChopItemsForSpaceInY(true);
            } else {
                descHeight -= descHeight % HEIGHT_OF_ONE_LINE_OF_DESCRIPTION_SECTION;
            }

            setDescriptionHeight(descHeight);
        }
    };

    const oOfferPreviewFormatter: OfferPreviewFormatter = useMemo(
        () => new OfferPreviewFormatter(offer),
        [offer],
    );

    const thuzzRatingDiv = () => {
        const thuzzRating = oOfferPreviewFormatter.getThuzzRating();

        return (
            thuzzRating && (
                <div className="thuzz-rating">
                    <span
                        className={`thuzz-value ${thuzzRating.type.toLowerCase().replace(' ', '-')}`}
                    >
                        {thuzzRating.value}
                    </span>
                    {thuzzRating.type}
                </div>
            )
        );
    };

    const previewImageDiv = () => {
        return (
            <PreviewImage
                src={offer.contentPreview?.episodePreviewImageUrl || ''}
                isAdult={oOfferPreviewFormatter.isAdult()}
                isMovie={oOfferPreviewFormatter.hasMoviePreview()}
                fallbackText={
                    (oOfferPreviewFormatter.hasMoviePreview()
                        ? offer.contentPreview?.title
                        : offer.contentPreview?.episodeTitle || offer.contentPreview?.title) || ''
                }
            />
        );
    };

    const statusMessageDiv = () => {
        const recordingStatusMessage = oOfferPreviewFormatter.getRecordingStatusMessage();

        return (
            recordingStatusMessage && (
                <div className="recording-status">
                    <span
                        className={`recording-icon ${
                            recordingStatusMessage.type === RecordingStatus.InProgressRecording
                                ? 'recording-progress'
                                : 'recording-partial'
                        }`}
                    />
                    <span className="recording-message">{recordingStatusMessage.message}</span>
                </div>
            )
        );
    };

    const contentAiringInfoDiv = () => (
        <div className="content-air-info">{oOfferPreviewFormatter.getContentAiringDateTime()}</div>
    );

    const channelInfoDiv = () =>
        offer.contentPreview?.channel && (
            <div className="channel-info">
                <span>{offer.contentPreview.channel.channelNumber}</span>
                <span className="resolution">{offer.contentPreview.channel.videoResolution}</span>
                <span>{offer.contentPreview.channel.callSign}</span>
            </div>
        );

    const titleDiv = () => {
        const isTitleOverflowed =
            (titleRef.current?.scrollWidth || 0) > (titleRef.current?.clientWidth || 0);
        const isEpisodeTitleOverflowed =
            (episodeTitleRef.current?.scrollWidth || 0) >
            (episodeTitleRef.current?.clientWidth || 0);
        const episodeTitle = oOfferPreviewFormatter.isAdult()
            ? ''
            : oOfferPreviewFormatter.getEpisodeTitle();
        const isMoviePreview = oOfferPreviewFormatter.hasMoviePreview();
        const title = oOfferPreviewFormatter.isAdult()
            ? Locale.getString(LocaleKeys.GUIDE_TITLE_HIDDEN)
            : offer.contentPreview?.title;

        return (
            <div className="show-title">
                <div
                    ref={titleRef}
                    className="title"
                    style={{ maxWidth: isMoviePreview ? '22.91vw' : '27.29vw' }}
                >
                    {title}
                    {isMoviePreview && (
                        <span
                            className="movie-year"
                            style={
                                isTitleOverflowed ? { position: 'absolute', right: '0.26vw' } : {}
                            }
                        >
                            {oOfferPreviewFormatter.getMovieYear()}
                        </span>
                    )}
                </div>
                {episodeTitle !== '' && (
                    <div
                        ref={episodeTitleRef}
                        className="episode-title"
                        data-overflow={isEpisodeTitleOverflowed ? '’' : ''}
                    >
                        {episodeTitle}
                    </div>
                )}
            </div>
        );
    };

    const descriptionDiv = () => {
        const ccVideoResolutionText = oOfferPreviewFormatter.getCcVideoResolution();
        const isDescriptionOverflowed =
            (descriptionRef.current?.scrollHeight || 0) > Math.ceil(descriptionHeight);

        return (
            <div ref={descriptionRef} className="description">
                {oOfferPreviewFormatter.isAdult() ? (
                    Locale.getString(LocaleKeys.GUIDE_DESCRIPTION_HIDDEN)
                ) : (
                    <>
                        {offer.contentPreview?.description}
                        <span
                            className="feature-attributes"
                            style={
                                isDescriptionOverflowed
                                    ? {
                                          position: 'absolute',
                                          bottom: 0,
                                          right: 0,
                                          display: 'flex',
                                          alignItems: 'center',
                                      }
                                    : {}
                            }
                        >
                            {isDescriptionOverflowed && <span className="transparent-effect" />}
                            {ccVideoResolutionText.length > 0 && (
                                <span className="cc_stream">{ccVideoResolutionText}</span>
                            )}
                            {isDescriptionOverflowed && <span className="info-btn" />}
                        </span>
                    </>
                )}
            </div>
        );
    };

    const parentalRatingAndGeneresDiv = () => {
        const internalRating = oOfferPreviewFormatter.getInternalRatings();
        const categoryLabel = offer.contentPreview?.categoryLabel?.split(',');
        const isParentalGenesOverflowed =
            (parentalGeneresRef.current?.scrollHeight || 0) >
            (parentalGeneresRef.current?.clientHeight || 0);

        return (
            <div ref={parentalGeneresRef} className="parental-genres">
                {internalRating && internalRating.length > 0 && (
                    <div className="parental-rating">
                        {internalRating.map((ratingName, index) => {
                            return (
                                <span className="internal-ratings" key={index}>
                                    {ratingName}
                                </span>
                            );
                        })}
                    </div>
                )}
                {!isParentalGenesOverflowed && categoryLabel && categoryLabel.length > 0 && (
                    <div className="catergories">{categoryLabel.join(', ')}</div>
                )}
            </div>
        );
    };

    const durationAndStarRatingOrFirstAiredInfoDiv = useMemo(() => {
        const duration = oOfferPreviewFormatter.getRecordingDurationTime();
        const hasStarRating = oOfferPreviewFormatter.hasStarRating();
        const firstAired = oOfferPreviewFormatter.getFormattedFirstAired();
        let starOrFirstAired = <></>;

        if (oOfferPreviewFormatter.hasMoviePreview() && hasStarRating) {
            const starRating = oOfferPreviewFormatter.getStarRating();

            starOrFirstAired = (
                <span className="star-rating">
                    {new Array(Math.floor(starRating)).fill(0).map((_, index) => (
                        <span className="star-icon full" key={index}></span>
                    ))}
                    {starRating > Math.floor(starRating) && (
                        <span className="star-icon half"></span>
                    )}
                    {new Array(STAR_RATING_MAX_STARS - Math.floor(starRating))
                        .fill(0)
                        .map((_, index) => {
                            return <span className="star-icon empty" key={index}></span>;
                        })}
                </span>
            );
        } else if (firstAired) {
            starOrFirstAired = <span className="first-aired">{firstAired}</span>;
        }

        return (
            <>
                {duration && <span>{duration}</span>}
                {duration && (hasStarRating || firstAired) && ' | '}
                {starOrFirstAired}
            </>
        );
    }, [oOfferPreviewFormatter]);

    const providerImagesDiv = useMemo(() => {
        const channelLogoUrl = oOfferPreviewFormatter.getChannelLogoUrl(48, 48);
        const providerUrls = oOfferPreviewFormatter.getProvidersImageUrls();

        return (
            ((DeviceFeatures.isSocuEnabled() && providerUrls && providerUrls.length > 0) ||
                channelLogoUrl) && (
                <div className="provider-images">
                    {channelLogoUrl && (
                        <ImageLoader
                            src={channelLogoUrl}
                            imageType={ImageLoaderTypes.PROVIDER_IMAGE_PREVIEW_ICON}
                        />
                    )}
                    {providerUrls?.map((url: string, index: number) => (
                        <ImageLoader
                            key={index}
                            src={url}
                            imageType={ImageLoaderTypes.PROVIDER_IMAGE_PREVIEW_ICON}
                        />
                    ))}
                </div>
            )
        );
    }, [oOfferPreviewFormatter]);

    // TODO: We need to eliminate offer in dependency (Find new approach)
    useEffect(() => {
        resize();
    }, [offer]);

    return (
        <React.Fragment>
            <div ref={previewRef} className="myshows-paused-preview">
                <div ref={topSectionRef} className="preview-top-section">
                    {!oOfferPreviewFormatter.isAdult() && thuzzRatingDiv()}

                    {previewImageDiv()}

                    {statusMessageDiv()}

                    {contentAiringInfoDiv()}

                    {!chopItemsForSpaceInY && channelInfoDiv()}

                    {titleDiv()}
                </div>

                <div
                    ref={middleSectionRef}
                    className="preview-middle-section"
                    style={isPreviewOverflowedInY ? { height: descriptionHeight } : {}}
                >
                    {descriptionDiv()}
                </div>

                {!oOfferPreviewFormatter.isAdult() && (
                    <div ref={bottomSectionRef} className="preview-bottom-section">
                        {!chopItemsForSpaceInY && parentalRatingAndGeneresDiv()}

                        {!chopItemsForSpaceInY && durationAndStarRatingOrFirstAiredInfoDiv}

                        {providerImagesDiv}
                    </div>
                )}
            </div>
        </React.Fragment>
    );
};

export default TiVoMemo(Preview);
